import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import styles from './SingleItemSelector.sass'

export interface SingleItemSelectorProps<T> {
  model: T | undefined
  itemOptions: T[]
  itemToString: (item: T) => string
  itemPrefix?: (item: T) => React.ReactNode
  onChange: (item: T | undefined) => void
  placeholder?: string
  disabled?: boolean
  dataTest?: string
}

export const SingleItemSelector = observer(function <T>({
  model,
  itemOptions,
  itemToString,
  itemPrefix,
  onChange,
  placeholder,
  disabled,
  dataTest,
}: SingleItemSelectorProps<T>) {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static(itemOptions, (item, text) =>
        itemToString(item).toLowerCase().includes(text.toLowerCase()),
      ),
    [itemOptions],
  )
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })
  return (
    <CustomSelect
      {...selectProps}
      value={model}
      itemToString={itemToString}
      onChange={onChange}
      multi={false}
      placeholder={placeholder}
      disabled={disabled}
      data-test={dataTest}
      renderItem={({
        item,
        toggleMenu,
        focusInputField,
        removeItem,
        ...itemProps
      }) => (
        <SelectItem prefix={itemPrefix && itemPrefix(item)} {...itemProps}>
          {itemToString(item)}
        </SelectItem>
      )}
      renderSearchItem={({ item, ...itemProps }) => (
        <SelectItem prefix={itemPrefix && itemPrefix(item)} {...itemProps}>
          {itemToString(item)}
        </SelectItem>
      )}
    />
  )
})

export interface SelectItemProps {
  children?: React.ReactNode
  highlighted?: boolean
  selected?: boolean
  prefix?: React.ReactNode
  itemToString: any
}

export const SelectItem = React.forwardRef(function (
  {
    children,
    highlighted,
    selected,
    prefix,
    itemToString,
    ...rest
  }: SelectItemProps,
  ref: React.Ref<any>,
) {
  return (
    <div
      className={cx(
        styles.selectItem,
        highlighted && styles.highlighted,
        selected && styles.selected,
      )}
      ref={ref}
      {...rest}
    >
      {prefix && <div className={styles.prefix}>{prefix}</div>}
      <div className={styles.selectItemText}>{children}</div>
    </div>
  )
})
